/**
 * dockPointer — makes the on-screen dock clickable / tappable (for tablet use).
 *
 * The whole instrument is keyboard-driven through one seam: DOM key events →
 * KeyState → the controllers → paramBus. Rather than duplicate any of that, a
 * pointer tap is adapted straight into that same seam: pressing a dock cell calls
 * `keyState.press(key, …)` and releasing calls `keyState.release(key, …)`, using a
 * real (undispatched) KeyboardEvent so the existing handlers get `shiftKey` /
 * `preventDefault` for free. Every downstream controller — effects, both modifier
 * knobs, the palette, combos, tempo — reacts exactly as it does to a physical key,
 * and the one-way paramBus rule is untouched (KeyState itself needs no changes).
 *
 * Cells carry a `data-key` = their normalized key identity (set in main.ts). Most
 * are momentary (press on pointerdown, release on pointerup); with multi-touch,
 * holding one cell while tapping another chords for free (each is an independent
 * held key). Two controls are "sticky" so single-pointer use works too:
 *
 *   • Shift  — a tap toggles an armed state (shown on both Shift cells) instead of
 *     a momentary hold. The next effect tap reads it as Shift+key (latch), then the
 *     arm clears — OS sticky-keys style.
 *   • − / +  — tuning needs a held target. On a +/- press with nothing tunable held,
 *     the last-tapped tunable key is briefly re-held for the duration of the press,
 *     so the existing per-frame ramp has something to scrub (tap = nudge, hold =
 *     ramp), matching the real-keyboard feel. Skipped when a key is already held.
 */

import { KeyState } from './keyState';
import { ModifierController } from './modifierController';
import { PaletteController } from './paletteController';
import { effectByKey } from '../effects/effectConfig';
import { visualByKey } from '../effects/visualConfig';
import { paletteByKey } from '../effects/presetConfig';

const SHIFT_KEYS = new Set(['ShiftLeft', 'ShiftRight']);
const PLUSMINUS_KEYS = new Set(['Minus', 'Equal']);

/** True for the keys whose held state is the +/- tune target (effects, visuals, palettes). */
function isTunable(key: string): boolean {
  return effectByKey.has(key) || visualByKey.has(key) || paletteByKey.has(key);
}

export interface DockPointerDeps {
  /** The #keyboard element whose `[data-key]` cells become tappable. */
  readonly root: HTMLElement;
  readonly keyState: KeyState;
  /** The effect modifier knob (its `.target` tells us if a tunable key is held). */
  readonly modifiers: ModifierController;
  /** The base-visual modifier knob. */
  readonly visualMod: ModifierController;
  /** The palette controller (owns its own +/- capture + held-type target). */
  readonly palette: PaletteController;
  /** The Shift keycaps, lit with `.armed` while sticky-shift is armed. */
  readonly shiftCells: readonly HTMLElement[];
}

/** Bind pointer handlers to every `[data-key]` cell under `root`. */
export function attachDockPointer(deps: DockPointerDeps): void {
  const { root, keyState, modifiers, visualMod, palette, shiftCells } = deps;

  let stickyShift = false;
  let lastTunable: string | undefined;

  const setArmed = (on: boolean): void => {
    stickyShift = on;
    for (const c of shiftCells) c.classList.toggle('armed', on);
  };
  const shiftActive = (): boolean =>
    stickyShift || keyState.isDown('ShiftLeft') || keyState.isDown('ShiftRight');
  const anyTuneTargetHeld = (): boolean =>
    modifiers.target !== undefined || visualMod.target !== undefined || palette.target !== undefined;

  root.querySelectorAll<HTMLElement>('[data-key]').forEach((cell) => {
    const key = cell.dataset.key;
    if (key === undefined) return;
    const isShift = SHIFT_KEYS.has(key);
    const isPlusMinus = PLUSMINUS_KEYS.has(key);
    const tunable = isTunable(key);

    // One pointer owns a cell at a time; `autoHeld` is any tunable key re-held to
    // give a +/- press a target (released together with the +/- key).
    let activePointer: number | undefined;
    let autoHeld: string | undefined;

    const down = (e: PointerEvent): void => {
      e.preventDefault();
      if (activePointer !== undefined) return;
      activePointer = e.pointerId;
      cell.setPointerCapture?.(e.pointerId);

      if (isShift) {
        setArmed(!stickyShift); // tap toggles the sticky-shift arm
        return;
      }

      if (isPlusMinus) {
        if (!anyTuneTargetHeld() && lastTunable !== undefined) {
          autoHeld = lastTunable;
          keyState.press(autoHeld, new KeyboardEvent('keydown'));
        }
        keyState.press(key, new KeyboardEvent('keydown'));
        return;
      }

      if (tunable) lastTunable = key;
      keyState.press(key, new KeyboardEvent('keydown', { shiftKey: shiftActive() }));
      if (stickyShift && effectByKey.has(key)) setArmed(false); // consumed by the effect
    };

    const up = (): void => {
      if (activePointer === undefined) return;
      activePointer = undefined;
      if (isShift) return; // toggle-only — nothing was held
      keyState.release(key, new KeyboardEvent('keyup'));
      if (autoHeld !== undefined) {
        keyState.release(autoHeld, new KeyboardEvent('keyup'));
        autoHeld = undefined;
      }
    };

    cell.addEventListener('pointerdown', down);
    cell.addEventListener('pointerup', up);
    cell.addEventListener('pointercancel', up);
  });
}
